
// Chart.js imports
import type { PerformanceChartProps, WeeklyAverage } from '../../types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export function PerformanceChart({ weeklyData, playerName, loading = false }: PerformanceChartProps) {
  // Show loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!weeklyData || weeklyData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Weekly Performance Trend
        </h3>
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-600 mb-2">No Performance Data</h4>
          <p className="text-gray-500">
            No weekly performance data available for {playerName}.
          </p>
        </div>
      </div>
    );
  }

  // Sort data by week number to ensure proper chronological order
  const sortedData = [...weeklyData].sort((a, b) => a.week_number - b.week_number);

  // Prepare chart data
  const chartData = {
    labels: sortedData.map((week: WeeklyAverage) => `Week ${week.week_number}`),
    datasets: [
      {
        label: `${playerName} Average`,
        data: sortedData.map((week: WeeklyAverage) => week.average_score),
        borderColor: 'rgb(59, 130, 246)', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)', // blue-500 with opacity
        tension: 0.4, // Smooth curve
        fill: true,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  // Calculate statistics for tooltips
  const bestWeek = sortedData.reduce((best, current) => 
    current.average_score > best.average_score ? current : best
  );
  const lowestWeek = sortedData.reduce((lowest, current) => 
    current.average_score < lowest.average_score ? current : lowest
  );
  const mostRecentWeek = sortedData.reduce((latest, current) => 
    current.week_number > latest.week_number ? current : latest
  );

  // Chart options with responsive design and custom tooltips
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(59, 130, 246, 0.5)',
        borderWidth: 1,
        callbacks: {
          title: (context: any) => {
            const dataIndex = context[0].dataIndex;
            const week = sortedData[dataIndex];
            return `Week ${week.week_number} (${new Date(week.week_date).toLocaleDateString()})`;
          },
          label: (context: any) => {
            const dataIndex = context.dataIndex;
            const week = sortedData[dataIndex];
            return [
              `Average: ${week.average_score.toFixed(1)}`,
              `Games Played: ${week.games_played}`,
              `Total Pins: ${week.total_pins}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Week',
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Average Score',
          font: {
            size: 14,
            weight: 'bold' as const,
          },
        },
        beginAtZero: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        // Set reasonable bounds for bowling scores
        min: Math.max(0, Math.min(...sortedData.map(w => w.average_score)) - 20),
        max: Math.min(300, Math.max(...sortedData.map(w => w.average_score)) + 20),
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart' as const,
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Weekly Performance Trend
        </h3>
        <p className="text-sm text-gray-600">
          {playerName}'s bowling average progression over {sortedData.length} weeks
        </p>
      </div>
      
      <div className="h-64 sm:h-72 md:h-80 lg:h-96">
        <Line data={chartData} options={options} />
      </div>
      
      {/* Summary statistics */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <div className="text-xl sm:text-2xl font-bold text-blue-600">
            {sortedData.length}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Weeks</div>
        </div>
        <div 
          className="text-center cursor-help" 
          title={`Week ${bestWeek.week_number} (${new Date(bestWeek.week_date).toLocaleDateString()})`}
        >
          <div className="text-xl sm:text-2xl font-bold text-green-600">
            {bestWeek.average_score.toFixed(1)}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Best Week Avg</div>
        </div>
        <div 
          className="text-center cursor-help" 
          title={`Week ${lowestWeek.week_number} (${new Date(lowestWeek.week_date).toLocaleDateString()})`}
        >
          <div className="text-xl sm:text-2xl font-bold text-orange-600">
            {lowestWeek.average_score.toFixed(1)}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Lowest Week Avg</div>
        </div>
        <div 
          className="text-center cursor-help" 
          title={`Based on Week ${mostRecentWeek.week_number} data: ${mostRecentWeek.total_pins} pins in ${mostRecentWeek.games_played} games`}
        >
          <div className="text-xl sm:text-2xl font-bold text-purple-600">
            {(mostRecentWeek.total_pins / mostRecentWeek.games_played).toFixed(1)}
          </div>
          <div className="text-xs sm:text-sm text-gray-600">Season Avg</div>
        </div>
      </div>
    </div>
  );
}